/**
 * What opens the search palette from the keyboard.
 *
 * The decision is a pure function over the bits of the event that matter, so the awkward cases —
 * "/" typed into a narrative field, Ctrl+K inside a textarea, Cmd+K on a Mac — are unit tests
 * rather than something you find out about from a support ticket.
 */

export interface HotkeyEvent {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  /** Whether the keystroke landed in something the user is typing into. */
  isEditable: boolean;
}

/**
 * Should this keystroke open the palette?
 *
 * TWO BINDINGS, and they are not interchangeable:
 *
 *  - **Ctrl+K / Cmd+K** works ANYWHERE, including inside a field. It is the binding people arrive
 *    with from every other tool, and one that stopped working whenever the cursor happened to be
 *    in a text box would read as broken rather than as careful. The caller must `preventDefault`:
 *    Cmd+K is the browser's own focus-the-address-bar in some builds, and Ctrl+K is "delete to end
 *    of line" in a field on macOS.
 *  - **`/`** works only when NOT typing — DESIGN_SYSTEM.md §9 asks for it, and it is the fast one
 *    because it needs no modifier. That is exactly why it has to yield to a field: `/` is a
 *    character, it appears in "4/9 Rimu Ave" and in half the dates anybody types, and a search box
 *    that eats it while somebody is entering an address would be unusable.
 *
 * `altKey` disqualifies both. Alt+K is a dead key for composed characters on several layouts, and
 * swallowing it would stop somebody typing their own name.
 */
export function opensSearch(event: HotkeyEvent): boolean {
  if (event.altKey) return false;

  const lowerK = event.key === 'k' || event.key === 'K';
  if (lowerK && (event.ctrlKey || event.metaKey)) return true;

  return event.key === '/' && !event.isEditable && !event.ctrlKey && !event.metaKey;
}

/**
 * How to write the shortcut for the person reading it: `⌘K` on Apple hardware, `Ctrl K` elsewhere.
 *
 * Both chords work everywhere (see `opensSearch`), so this is only about the label — but a Windows
 * operator shown `⌘K` is being taught a key their keyboard does not have. `userAgentData.platform`
 * is preferred where it exists; `navigator.platform` is deprecated but is still the only signal
 * Safari and Firefox give, and iPadOS with a keyboard is a Mac for this purpose.
 */
export function searchShortcutLabel(platform: string): string {
  return /mac|iphone|ipad|ipod/i.test(platform) ? '⌘K' : 'Ctrl K';
}

/** The browser's best guess at the platform string `searchShortcutLabel` reads. */
export function currentPlatform(): string {
  if (typeof navigator === 'undefined') return '';
  const uaData = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData;
  return uaData?.platform ?? navigator.platform;
}

/**
 * Whether the event landed somewhere the user is typing.
 *
 * `isContentEditable` is checked as well as the tag, because the rich fields in this product — a
 * correspondence template's body — are contenteditable rather than `<textarea>`, and `/` is a
 * perfectly ordinary character to type into one.
 *
 * A `<select>` counts as editable too: typing a letter in an open select jumps to the option
 * beginning with it, so `/` is doing a job there as well.
 */
export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;

  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}
